import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Star, Flag, EyeOff, Eye, Trash2, Search, SlidersHorizontal,
  RotateCcw, CheckCircle2, AlertTriangle, Award,
} from 'lucide-react';
import { adminApi } from '../../api/client';
import DashModal from '../../components/dashboard/DashModal';
import { StarsDisplay } from '../../components/common/Stars';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import '../../components/dashboard/DashForm.css';
import './AdminOrders.css';

const RECOMMEND_LABELS = { yes: 'Recommande', no: 'Ne recommande pas', unsure: 'Indécis' };
const STATUS_META = {
  pending: { label: 'En attente', bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
  published: { label: 'Publié', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  hidden: { label: 'Masqué', bg: 'var(--gray-50)', color: 'var(--gray-500)', border: 'var(--gray-200)' },
};

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState(null);
  const [translators, setTranslators] = useState([]);
  const [search, setSearch] = useState('');
  const [translatorFilter, setTranslatorFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [moderation, setModeration] = useState(null); // { action: 'hide'|'publish'|'delete', review }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => adminApi.clientReviews()
    .then((res) => setReviews(res.reviews || []))
    .catch(() => setReviews([]));

  useEffect(() => {
    reload();
    adminApi.translators()
      .then((res) => setTranslators(res.translators || res.users || []))
      .catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const all = reviews || [];
    return {
      total: all.length,
      flagged: all.filter((r) => r.flagged || r.status === 'pending').length,
      published: all.filter((r) => r.status === 'published').length,
      featured: all.filter((r) => r.featured && r.status === 'published').length,
    };
  }, [reviews]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (reviews || []).filter((r) => {
      if (translatorFilter !== 'all' && r.translatorId !== translatorFilter) return false;
      if (ratingFilter !== 'all' && r.rating !== Number(ratingFilter)) return false;
      if (statusFilter === 'flagged' && !r.flagged) return false;
      if (statusFilter === 'featured' && !r.featured) return false;
      if (statusFilter !== 'all' && statusFilter !== 'flagged' && statusFilter !== 'featured' && r.status !== statusFilter) return false;
      if (serviceFilter !== 'all' && r.orderServiceSlug !== serviceFilter && r.orderService !== serviceFilter) return false;
      if (!q) return true;
      return [r.orderCode, r.clientName, r.translatorName, r.comment, r.orderService, r.orderDocument, r.orderLangs]
        .some((x) => String(x || '').toLowerCase().includes(q));
    });
  }, [reviews, search, translatorFilter, ratingFilter, statusFilter, serviceFilter]);

  // Sous-services distincts présents dans les avis (instantané prestation).
  const serviceOptions = useMemo(() => {
    const map = new Map();
    (reviews || []).forEach((r) => {
      const key = r.orderServiceSlug || r.orderService;
      if (key) map.set(key, r.orderService || key);
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [reviews]);

  const pg = usePagination(filtered, 8);
  const hasFilters = search || translatorFilter !== 'all' || ratingFilter !== 'all' || statusFilter !== 'all' || serviceFilter !== 'all';
  const resetFilters = () => { setSearch(''); setTranslatorFilter('all'); setRatingFilter('all'); setStatusFilter('all'); setServiceFilter('all'); };

  const openModeration = (action, review) => { setNote(''); setModeration({ action, review }); };

  const runModeration = async () => {
    if (!moderation) return;
    if (moderation.action === 'delete' && !note.trim()) return;
    setBusy(true);
    try {
      if (moderation.action === 'delete') {
        await adminApi.deleteClientReview(moderation.review.id, note.trim());
      } else {
        await adminApi.moderateClientReview(moderation.review.id, {
          action: moderation.action,
          moderationNote: note.trim(),
        });
      }
      setModeration(null);
      setNote('');
      reload();
    } catch {
      setModeration(null);
    } finally {
      setBusy(false);
    }
  };

  const kpis = [
    { label: 'Total des avis', value: stats.total, icon: Star, cls: 'ah-kpi--teal' },
    { label: 'En attente de modération', value: stats.flagged, icon: Flag, cls: 'ah-kpi--amber' },
    { label: 'Publiés', value: stats.published, icon: CheckCircle2, cls: 'ah-kpi--green' },
    { label: 'Sur la page d’accueil', value: stats.featured, icon: Award, cls: 'ah-kpi--navy' },
  ];

  return (
    <div className="ah">
      <header className="ah-hero">
        <div className="ah-hero-text">
          <span className="ah-hero-eyebrow"><Star size={13} />Avis et évaluations</span>
          <h1>Avis des clients</h1>
          <p>Consultez, modérez les évaluations des clients et choisissez celles affichées sur la page d’accueil.</p>
        </div>
      </header>

      <div className="ah-kpis">
        {kpis.map((k) => (
          <div className={`ah-kpi ${k.cls}`} key={k.label}>
            <span className="ah-kpi-icon"><k.icon size={22} /></span>
            <div className="ah-kpi-body">
              <em>{k.label}</em>
              <strong>{k.value}</strong>
            </div>
          </div>
        ))}
      </div>

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <span>{filtered.length} / {(reviews || []).length}</span>
            {hasFilters && (
              <button type="button" className="of-reset" onClick={resetFilters}>
                <RotateCcw size={13} />Réinitialiser
              </button>
            )}
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={14} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Commande, client, traducteur, commentaire…"
                style={{ flex: 1 }}
              />
            </div>
          </div>
          <div className="of-field">
            <label>Traducteur</label>
            <select value={translatorFilter} onChange={(e) => setTranslatorFilter(e.target.value)}>
              <option value="all">Tous</option>
              {translators.map((u) => (
                <option key={u.id || u._id} value={u.id || u._id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className="of-field">
            <label>Sous-service</label>
            <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
              <option value="all">Tous</option>
              {serviceOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="of-field">
            <label>Note</label>
            <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}>
              <option value="all">Toutes</option>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} / 5</option>)}
            </select>
          </div>
          <div className="of-field">
            <label>Statut</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="pending">En attente</option>
              <option value="published">Publiés</option>
              <option value="featured">Sur la page d’accueil</option>
              <option value="flagged">Signalés</option>
              <option value="hidden">Masqués</option>
            </select>
          </div>
        </div>
      </div>

      {reviews === null ? (
        <p className="dash-empty">Chargement…</p>
      ) : pg.pageItems.length === 0 ? (
        <p className="dash-empty">Aucun avis ne correspond à ces critères.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pg.pageItems.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.hidden;
            return (
              <div
                key={r.id}
                className="dash-panel"
                style={{
                  padding: '14px 16px',
                  border: `1px solid ${r.flagged ? '#fdba74' : 'var(--gray-100)'}`,
                  background: r.status === 'hidden' ? 'var(--gray-50)' : r.flagged ? '#fff7ed' : 'var(--white)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <StarsDisplay value={r.rating} size={14} showValue />
                    <strong style={{ fontSize: '0.85rem' }}>{r.orderCode}</strong>
                    <span
                      style={{
                        fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px', borderRadius: 999,
                        background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
                      }}
                    >
                      {meta.label}
                    </span>
                    {r.flagged && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: '#ffedd5', color: '#c2410c', border: '1px solid #fdba74' }}>
                        <Flag size={10} style={{ verticalAlign: '-1px' }} /> Signalé
                      </span>
                    )}
                    {r.featured && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: '#fef9c3', color: '#a16207', border: '1px solid #fde68a' }}>
                        <Award size={10} style={{ verticalAlign: '-1px' }} /> Page d’accueil
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : ''}
                  </span>
                </div>

                <p style={{ margin: '8px 0 0', fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                  Client : <strong>{r.clientName || '—'}</strong>
                  {' '}· Traducteur :{' '}
                  {r.translatorId ? (
                    <Link to={`/admin/translators/${r.translatorId}`} className="dash-link">{r.translatorName || '—'}</Link>
                  ) : (
                    <strong>{r.translatorName || '—'}</strong>
                  )}
                  {r.recommend ? ` · ${RECOMMEND_LABELS[r.recommend] || ''}` : ''}
                  {(r.orderService || r.orderDocument || r.orderLangs) && (
                    <>
                      {' '}· <span>{[r.orderService, r.orderDocument, r.orderLangs].filter(Boolean).join(' · ')}</span>
                    </>
                  )}
                </p>

                {r.comment && (
                  <p style={{ margin: '8px 0 0', fontSize: '0.88rem', color: 'var(--gray-700)', lineHeight: 1.55 }}>
                    « {r.comment} »
                  </p>
                )}

                {r.flagged && r.flagReason && (
                  <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#c2410c', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Flag size={11} /> Signalement du professionnel : {r.flagReason}
                  </p>
                )}
                {r.status === 'hidden' && r.moderationNote && (
                  <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                    Motif : {r.moderationNote}
                  </p>
                )}

                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {r.status !== 'published' ? (
                    <button
                      type="button"
                      className="dash-btn-sm"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#047857' }}
                      onClick={() => openModeration('publish', r)}
                    >
                      <Eye size={12} /> Publier
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="dash-btn-sm"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#92400e' }}
                      onClick={() => openModeration('hide', r)}
                    >
                      <EyeOff size={12} /> Masquer
                    </button>
                  )}
                  <button
                    type="button"
                    className="dash-btn-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#b91c1c' }}
                    onClick={() => openModeration('delete', r)}
                  >
                    <Trash2 size={12} /> Supprimer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} />
      </div>

      <DashModal
        open={Boolean(moderation)}
        title={
          {
            delete: 'Supprimer cet avis',
            hide: 'Masquer cet avis',
            publish: 'Publier cet avis',
          }[moderation?.action] || ''
        }
        subtitle={moderation ? `${moderation.review.orderCode} — ${moderation.review.rating}/5 · ${moderation.review.clientName}` : ''}
        onClose={() => !busy && setModeration(null)}
      >
        {moderation && (
          <div className="dash-form" style={{ maxWidth: '100%' }}>
            {moderation.action === 'delete' ? (
              <p style={{ marginTop: 0, display: 'flex', alignItems: 'flex-start', gap: 8, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', fontSize: '0.85rem' }}>
                <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2 }} />
                Réservé aux contenus abusifs. L’avis ne sera plus visible nulle part ;
                le motif est obligatoire et conservé dans l’historique de modération.
              </p>
            ) : moderation.action === 'hide' ? (
              <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                L’avis ne sera plus affiché publiquement, il est retiré de la page d’accueil
                et ne compte plus dans la note moyenne.
              </p>
            ) : (
              <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                L’avis redevient public et compte dans la note moyenne. Un éventuel signalement est soldé.
              </p>
            )}
            {moderation.action === 'delete' && (
              <div className="dash-field">
                <label>Motif de modération *</label>
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            )}
            <div className="dash-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || (moderation.action === 'delete' && !note.trim())}
                onClick={runModeration}
              >
                {busy ? 'Traitement…'
                  : {
                      delete: 'Supprimer',
                      hide: 'Masquer',
                      publish: 'Publier',
                    }[moderation.action]}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setModeration(null)} disabled={busy}>
                Annuler
              </button>
            </div>
          </div>
        )}
      </DashModal>
    </div>
  );
}
